import { AxlesTable } from '@src/axles/AxlesTable';
import {
  ReadOnlyAxlesRow,
  ReadOnlyAxlesTable,
} from '@src/axles/ReadOnlyAxlesTable';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { useMeasurement } from '@src/common/measurements/useMeasurement';
import { theme } from '@src/common/styles/Theme';
import {
  type AxleSummaryGroup,
  formatAxleGroupName,
} from '@src/orders/domain/AxleSummary';
import type { Order } from '@src/orders/domain/Order';
import { AxleInformationRow } from '@src/orders/PermitFormSections/axlesConfiguration/AxleInformationRow';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import { AxleFormName } from '@src/permitField/AxleInformationFields';
import { TractorAxlesFormUtils } from '@src/permitField/TractorAxlesFormUtils';
import {
  TrailerFormUtils,
  TrailersFormName,
} from '@src/permitField/TrailerInformationFields';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type AxleGroupProps = {
  axleGroup: AxleSummaryGroup;
  groupIndex: number;
  cumulativeAxleCount: number;
  isLoading?: boolean;
  order: Order;
  fields: string[];
};

export const AxleSubSectionGroup = (props: AxleGroupProps) => {
  const { t } = useTranslation();
  const { isMetric } = useMeasurement();
  const sectionName = t(`axleGroup`, {
    groupName: formatAxleGroupName(props.axleGroup, t),
    count: props.axleGroup.axles.length,
  });
  const { disabledFields, isFieldDisabled } = useDisabledFields();
  const hasAtLeastOneFieldDisabled = props.fields.some(isFieldDisabled);

  const form = useFormContext();
  const readOnly = form.formState.disabled;

  const vehiclePrefix =
    props.axleGroup.groupType === 'TRACTOR'
      ? TractorAxlesFormUtils.fieldName
      : TrailerFormUtils.pathToTrailer(props.groupIndex - 1);

  const missingAxlesError =
    props.axleGroup.groupType === 'TRACTOR'
      ? form.formState.errors[TractorAxlesFormUtils.fieldName]
      : // @ts-ignore
        form.formState.errors[TrailersFormName]?.[props.groupIndex - 1]?.[
          AxleFormName
        ];

  return (
    <Stack marginBottom={theme.spacing['3']} gap={theme.spacing['2']}>
      <Typography component="h3" variant="h6">
        {sectionName}
      </Typography>
      {readOnly ? (
        <ReadOnlyAxlesTable
          isMetric={isMetric}
          name="axles"
          fields={props.fields}
          order={props.order}
        >
          {props.axleGroup.axles.map((_axle, i) => {
            const pathToAxles = vehiclePrefix
              ? `${vehiclePrefix}.${AxleFormName}`
              : AxleFormName;
            const pathToAxle = `${pathToAxles}.${i}`;
            return (
              <ReadOnlyAxlesRow
                isMetric={isMetric}
                key={`axle-${i}-information`}
                fields={props.fields}
                axleIndex={i}
                axle={form.getValues(pathToAxle)}
              />
            );
          })}
        </ReadOnlyAxlesTable>
      ) : (
        <AxlesTable
          name={sectionName}
          fields={props.fields}
          axleCount={props.axleGroup.axles.length}
          setValue={form.setValue}
          getValue={form.getValues}
          vehiclePrefix={vehiclePrefix}
          missingAxlesError={missingAxlesError}
          addAxleDisabled={hasAtLeastOneFieldDisabled}
        >
          {props.axleGroup.axles.map((_, i) => (
            <AxleInformationRow
              key={`axle-${i}-information`}
              index={i}
              cumulativeAxleCount={props.cumulativeAxleCount}
              isLoading={props.isLoading}
              fields={props.fields}
              vehicleType={props.axleGroup.groupType}
              vehiclePrefix={vehiclePrefix}
              setValue={form.setValue}
              getValue={form.getValues}
              totalAxles={props.axleGroup.axles.length}
              disabledFields={disabledFields}
            />
          ))}
        </AxlesTable>
      )}
    </Stack>
  );
};
